/**
 * Upload Schema — V1 Beneficiaries (Bulk Upload)
 *
 * Creates beneficiaries via the V1 /entities endpoint.
 * Uses customSubmit for per-row POSTs since V1 has no bulk-create.
 */

import { UploadSchema } from '../types';

async function submitBeneficiary({
	row,
	projectId,
	apiBaseUrl,
	headers,
	signal,
}: {
	row: Record<string, unknown>;
	projectId: number;
	apiBaseUrl: string;
	headers: Record<string, string>;
	signal?: AbortSignal;
}): Promise<{ id: number; name?: string }> {
	// Build the name from explicit name or firstName+lastName
	const name =
		row.name ||
		[row.firstName, row.lastName].filter(Boolean).join(' ') ||
		`Beneficiary-${Date.now()}`;

	// Build loans array for financing sources
	const loans: Array<{ id: number; parent: number | null }> = [];
	if (row.loanId) {
		loans.push({
			id: Number(row.loanId),
			parent: row.entityId ? Number(row.entityId) : null,
		});
	}

	const payload = {
		name,
		type: 'BENEFICIARY',
		subtype: row.subtype || 'INDIVIDUAL',
		isKycVerified: true,
		project: projectId,
		loans,
		...(row.idExternal ? { idExternal: String(row.idExternal) } : {}),
		...(row.phone ? { phone: String(row.phone) } : {}),
		...(row.idWbg ? { idWbg: String(row.idWbg) } : {}),
		// Create a user account if firstName is provided
		...(row.firstName
			? {
					users: [
						{
							firstName: String(row.firstName),
							lastName: row.lastName ? String(row.lastName) : '',
							...(row.email && String(row.email).includes('@') ? { email: String(row.email) } : {}),
							...(row.phone ? { phone: String(row.phone) } : {}),
							role: 'GENERAL',
							projects: [{ id: projectId }],
						},
					],
				}
			: {}),
	};

	const res = await fetch(`${apiBaseUrl}/v1/entities`, {
		method: 'POST',
		headers,
		body: JSON.stringify(payload),
		signal,
	});

	if (!res.ok) {
		const errText = await res.text().catch(() => `HTTP ${res.status}`);
		let msg: string;
		try {
			const errJson = JSON.parse(errText);
			msg =
				errJson.error?.message || errJson.message || `Beneficiary creation failed (${res.status})`;
		} catch {
			msg = errText || `Beneficiary creation failed (${res.status})`;
		}
		throw new Error(msg);
	}

	const data = await res.json();
	if (data.error) {
		throw new Error(data.error.message || 'Beneficiary creation failed');
	}

	const entity = data.data?.[0] ?? data.data ?? data;
	return { id: entity.id, name: entity.name ?? String(entity.id) };
}

export const v1BeneficiarySchema: UploadSchema = {
	entityType: 'v1Beneficiary',
	label: 'Beneficiary',
	apiEndpoint: '/v1/entities',
	apiMethod: 'POST',
	customSubmit: submitBeneficiary,
	fields: [
		{
			name: 'name',
			label: 'Beneficiary Name',
			dataType: 'string',
			required: true,
			headerAliases: ['Entity Name', 'Recipient Name', 'Name', 'Beneficiary'],
		},
		{
			name: 'subtype',
			label: 'Beneficiary Type',
			dataType: 'enum',
			required: false,
			enumValues: ['INDIVIDUAL', 'HOUSEHOLD', 'ORGANIZATION', 'BANK', 'PRIVATE_COMPANY'] as const,
			defaultValue: 'INDIVIDUAL',
			headerAliases: ['Subtype', 'Type', 'Entity Type'],
		},
		{
			name: 'idExternal',
			label: 'Unique Identifier',
			dataType: 'string',
			required: true,
			headerAliases: ['External ID', 'Tax Number', 'SSN', 'NIN', 'ID Number'],
		},
		{
			name: 'entityId',
			label: 'Entity',
			dataType: 'relation',
			required: true,
			relation: {
				entityType: 'entity',
				matchField: 'name',
				idField: 'id',
				contextKey: 'entities',
				alternateMatchFields: ['idExternal'],
			},
			headerAliases: ['Parent Entity', 'PIU', 'Organization', 'Agency'],
		},
		{
			name: 'loanId',
			label: 'Financing Source',
			dataType: 'relation',
			required: true,
			relation: {
				entityType: 'loan',
				matchField: 'name',
				idField: 'id',
				contextKey: 'loans',
				alternateMatchFields: ['code'],
			},
			headerAliases: ['Loan', 'Loan Code', 'Financing', 'Fund Source'],
		},
		{
			name: 'firstName',
			label: 'First Name',
			dataType: 'string',
			required: false,
			headerAliases: ['Given Name', 'User First Name'],
		},
		{
			name: 'lastName',
			label: 'Last Name',
			dataType: 'string',
			required: false,
			headerAliases: ['Surname', 'Family Name', 'User Last Name'],
		},
		{
			name: 'email',
			label: 'Email',
			dataType: 'string',
			required: false,
			headerAliases: ['Email Address', 'E-mail'],
		},
		{
			name: 'phone',
			label: 'Phone',
			dataType: 'string',
			required: false,
			headerAliases: ['Phone Number', 'Telephone', 'Contact'],
		},
		{
			name: 'idWbg',
			label: 'WBG ID',
			dataType: 'string',
			required: false,
			headerAliases: ['World Bank ID', 'WB ID'],
		},
	],
};
